"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { parseJsonBody } from "@/lib/general.utils";
import useGeneralStore from "@/store/General/useGeneralStore";
import {
  Phone,
  Email,
  LocationOn,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { Button } from "@mui/material";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const DonateComponent = () => {
  const { t, i18n } = useTranslation("common");
  const router = useRouter();
  const { DonateComponent: donateData } = useGeneralStore((state) => ({
    DonateComponent: state.DonateComponent,
  }));
  const donateJson = parseJsonBody(donateData?.jsonBody);
  // Get data from database or fallback to defaults
  const contactInfo = donateJson?.contactInfo;
  const header = donateJson?.header;
  const buttonText = t("dashboard.donate.form.buttonTextStatic");
  const currency = t("dashboard.donate.form.currencyStatic");
  const galleryImages = donateJson?.galleryImages || [];

  const handleDonateClick = () => {
    router.push("/volunteer");
  };

  return (
    <div>
      {/* Header Section - Contact Information */}
      <div className="bg-white py-[2rem] px-[2rem] border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-[2rem]">
            {/* Phone */}
            <div
              className="flex items-center w-full md:w-auto gap-[0.75rem]"
              dir="rtl"
            >
              <Phone className="text-primary text-[1.5rem]" />
              <div className="flex flex-col">
                <span className="text-[0.875rem] text-gray-600">
                  {contactInfo?.phoneLabel}
                </span>
                <span className="text-[1rem] font-medium text-black">
                  {contactInfo?.phone}
                </span>
              </div>
            </div>

            {/* Email */}
            <div
              className="flex items-center w-full md:w-auto gap-[0.75rem]"
              dir="rtl"
            >
              <Email className="text-primary text-[1.5rem]" />
              <div className="flex flex-col">
                <span className="text-[0.875rem] text-gray-600">
                  {contactInfo?.emailLabel}
                </span>
                <span className="text-[1rem] font-medium text-black">
                  {contactInfo?.email}
                </span>
              </div>
            </div>

            {/* Address */}
            <div
              className="flex items-center w-full md:w-auto gap-[0.75rem]"
              dir="rtl"
            >
              <LocationOn className="text-primary text-[1.5rem]" />
              <div className="flex flex-col">
                <span className="text-[1rem] font-medium text-black max-w-[20rem]">
                  {contactInfo?.address}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Donation Section - Dark Blue Background */}
      <div className="bg-primary py-[4rem] px-[2rem]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-[3rem]"
            dir="rtl"
          >
            <h1 className="text-[2.5rem] md:text-[3rem] font-bold text-white mb-[1.5rem]">
              {header?.title || t("donate.title")}
            </h1>
            <p className="text-[1.2rem] md:text-[1.5rem] text-white leading-relaxed">
              {header?.subtitle || ""}
            </p>
          </motion.div>

          {/* Donation Input Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col-reverse md:flex-row gap-[1rem] items-center justify-center"
            dir="rtl"
          >
            <Button
              variant="contained"
              onClick={handleDonateClick}
              className="!bg-secondary !text-white !px-[2.5rem] !py-[1rem] !text-[1.1rem] !font-bold !rounded-lg hover:!bg-secondary/80 !transition-all w-full md:w-auto !min-w-[10rem]"
            >
              {buttonText}
            </Button>
            <div className="flex items-center gap-[1rem] bg-gray-100 rounded-[0.5rem] px-[1.5rem] md:px-[2rem] py-[0.8rem] md:py-[1rem] w-full md:w-auto">
              <input
                type="text"
                className="text-[1.1rem] sm:text-[1.3rem] border-none bg-transparent outline-none w-full md:w-[8rem]"
              />
              <span className="text-[1.1rem] sm:text-[1.3rem] text-[#575757] whitespace-nowrap">
                {t("currency")}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Image Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="bg-white py-[3rem] md:py-[5rem] px-[1rem] md:px-[2rem] w-full overflow-hidden">
          <div className="mx-auto max-w-[120rem] w-full">
            <div className="relative ">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                navigation={{
                  nextEl: ".gallery-button-next",
                  prevEl: ".gallery-button-prev",
                }}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 30,
                  },
                }}
                loop={galleryImages.length > 4}
                className="pb-[4rem] gallery-swiper"
              >
                {galleryImages.map((image: any, index: number) => (
                  <SwiperSlide key={index}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="relative aspect-[5/3] rounded-lg overflow-hidden"
                    >
                      <Image
                        src={image.url || image || ""}
                        alt={`Gallery image ${index + 1}`}
                        fill
                        className="object-cover"
                      />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Custom Navigation */}
              <button className="gallery-button-prev absolute left-0 md:left-[-1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-primary hover:bg-[#0a4384] rounded-full transition-colors shadow-lg">
                <ChevronLeft className="text-white text-[1.5rem] md:text-[2rem]" />
              </button>
              <button className="gallery-button-next absolute right-0 md:right-[-1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-primary hover:bg-[#0a4384] rounded-full transition-colors shadow-lg">
                <ChevronRight className="text-white text-[1.5rem] md:text-[2rem]" />
              </button>
            </div>

            {/* Custom Pagination Styles */}
            <style jsx global>{`
              .gallery-swiper .swiper-pagination {
                bottom: 1rem !important;
              }
              .gallery-swiper .swiper-pagination-bullet {
                width: 0.75rem !important;
                height: 0.75rem !important;
                background: rgba(26, 43, 107, 0.3) !important;
                opacity: 1 !important;
                margin: 0 0.25rem !important;
              }
              .gallery-swiper .swiper-pagination-bullet-active {
                background: #1a2b6b !important;
                width: 1.5rem !important;
                border-radius: 0.375rem !important;
              }
            `}</style>
          </div>
        </section>
      )}
    </div>
  );
};

export default DonateComponent;
